import { getEmptyCellData, isFormulaType } from '@doss/common-all'
import {
  Box,
  TableRow as MuiTableRow,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Theme,
  Typography,
  tableCellClasses,
} from '@mui/material'
import { useFormikContext } from 'formik'
import { orderBy } from 'lodash'
import { Children, useCallback, useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { Prompt } from '~/common/hooks/usePrompt'
import useStyles from '~/common/hooks/useStyles'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { checkFormikDirtyState, getAddRecordInitialValues } from '~/common/utils/formik'
import { localKeyMap } from '~/common/utils/keymap'
import { getIconByColumnType } from '~/common/utils/table'
import { AllowedArrayInnerTypes } from '~/common/utils/table-column'
import { RecordCellDetails } from '~/components/records/record-cell-details/RecordCellDetails'
import { AddRecordFormCell, AddRecordTableColumn } from '~/components/records/types'
import {
  EditableTableCellOriginProvider,
  RecordCellEditOrigin,
} from '~/components/table-cells/useEditableTableCellOriginProvider'
import { RecordDetailsCellPermissionsProvider } from '../RecordDetailsCellPermissionsProvider'
import { LookupCellValue, isLookupCellValue } from './AddRecordDialog'

function getStyles(theme: Theme, isMobile: boolean) {
  return {
    tableContainer: {
      borderRadius: '2px',
      display: 'flex',
      justifyContent: 'center',
    },
    table: {
      [`& .${tableCellClasses.root}`]: {
        borderBottom: 'none',
        [theme.breakpoints.down('sm')]: {
          display: 'block',
          padding: theme.spacing(1.5),
          '&:first-of-type': {
            paddingBottom: theme.spacing(0.5),
          },
          '&:last-of-type': {
            paddingTop: 0,
          },
        },
      },
    },
    tableRow: {
      [theme.breakpoints.down('sm')]: {
        display: 'block',
        marginBottom: theme.spacing(2),
      },
    },
    name: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    value: {
      width: '70%',
      ...(!isMobile && {
        maxWidth: 0,
        overflow: 'hidden',
      }),
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
  }
}

interface AddRecordFormProps {
  orderedColumns: AddRecordTableColumn[]
  handleCloseWithoutSaving: () => Promise<void>
  addDocumentId: (documentId: string) => void
  removeDocumentId: (documentId: string) => void
  childTableIds: string[]
  tableId: string
}

/**
 * Renders form field for editing a record when in edit mode on a table
 */
export function AddRecordForm({
  orderedColumns,
  handleCloseWithoutSaving,
  addDocumentId,
  removeDocumentId,
  childTableIds,
  tableId,
}: AddRecordFormProps) {
  const { t } = useTypedTranslation()
  const formikProps = useFormikContext<Record<string, unknown>>()
  const { isMobile } = useDeviceType()
  const styles = useStyles((theme) => getStyles(theme, isMobile))

  // Create a map of the current selected lookup row ids per column group
  const lookupColumnGroupSelectedLookupRows = useMemo(() => {
    const map = new Map<string, string[]>()

    orderedColumns.forEach((column) => {
      const lookupColumnGroupId = column.lookupColumnGroup?.id
      if (lookupColumnGroupId) {
        const columnId = column.id
        const formikValue = formikProps.values[columnId]

        if (formikValue) {
          // If the value is an array, check if it is an array of LookupCellValue
          if (Array.isArray(formikValue)) {
            const unknownArray = formikValue as unknown[]
            if (unknownArray.every((item): item is LookupCellValue => isLookupCellValue(item))) {
              map.set(
                lookupColumnGroupId,
                unknownArray.map((lookupCellValue) => lookupCellValue.row.id),
              )
            }
          }
        }
      }
    })

    return map
  }, [orderedColumns, formikProps])

  const ref = useHotkeys(
    [localKeyMap.CONTROL_ENTER, localKeyMap.COMMAND_ENTER, localKeyMap.ENTER],
    (event) => {
      if (event.key === localKeyMap.ENTER) {
        event.preventDefault()

        if (!event.ctrlKey && !event.metaKey) {
          event.stopPropagation()
          return
        }

        formikProps.submitForm()
      }
    },
    { enableOnFormTags: true },
  )

  const getSelectedLookupRowIds = useCallback(
    (cell: AddRecordFormCell) => {
      const selectedLookupRowIds: string[] = []
      if (cell.column.lookupColumnGroup) {
        const lookupColumnGroupLookupRows = lookupColumnGroupSelectedLookupRows.get(
          cell.column.lookupColumnGroup.id,
        )
        if (lookupColumnGroupLookupRows) {
          selectedLookupRowIds.push(...lookupColumnGroupLookupRows)
        }
      }
      return selectedLookupRowIds
    },
    [lookupColumnGroupSelectedLookupRows],
  )

  const rows = orderBy(orderedColumns, 'order', 'asc')
    .filter((column) => !isFormulaType(column.type))
    // Do not display lookup or array of lookup fields that are on the child table of the table
    // for which the record is being added
    .filter(
      (column) => !(column.lookupColumn && childTableIds.includes(column.lookupColumn.table.id)),
    )
    .map((column) => {
      const cell: AddRecordFormCell = {
        column,
        data: getEmptyCellData(column),
        userVisibleValue: null,
        documents: [],
        users: [],
      }
      return {
        column,
        value: (
          <RecordDetailsCellPermissionsProvider cell={cell} column={cell.column} tableId={tableId}>
            <RecordCellDetails
              addDocumentId={addDocumentId}
              cell={cell}
              columns={orderedColumns}
              lookupGroupId={cell.column.lookupColumnGroup?.id}
              removeDocumentId={removeDocumentId}
              selectedLookupRowIds={getSelectedLookupRowIds(cell)}
              showDirtyIndicator={false}
              tableId={tableId}
            />
          </RecordDetailsCellPermissionsProvider>
        ),
      }
    })

  const isDirty = checkFormikDirtyState(
    getAddRecordInitialValues(orderedColumns),
    formikProps.values,
  )
  return (
    <>
      <Prompt
        callback={handleCloseWithoutSaving}
        message={t('common.leave_page_warning')}
        shouldConfirm={isDirty}
      />
      <EditableTableCellOriginProvider origin={RecordCellEditOrigin.RECORD_CREATE}>
        <Box ref={ref} component="form" onSubmit={formikProps.handleSubmit}>
          <TableContainer sx={styles.tableContainer}>
            <Table aria-label="records table" sx={styles.table}>
              <TableBody>
                {Children.toArray(
                  rows.map((row) => (
                    <MuiTableRow key={row.column.id} sx={styles.tableRow}>
                      <TableCell component="th" scope="row" sx={styles.name}>
                        <Box className="flex items-center gap-1 pt-1">
                          {getIconByColumnType(
                            row.column.type,
                            row.column.innerType as AllowedArrayInnerTypes,
                          )}
                          <Typography>
                            {row.column.name}
                            {!row.column.isNullable && <span className="ps-1 text-red-9">*</span>}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={styles.value}>{row.value}</TableCell>
                    </MuiTableRow>
                  )),
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </EditableTableCellOriginProvider>
    </>
  )
}
